import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders } from './sharedStyles';

const POLL_INTERVAL = 5000;

export default function SystemHealthPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [system, setSystem] = useState(null);

  const fetchData = useCallback(() => {
    fetch(`${API_BASE}/api/v1/monitor/system`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : null)
      .then(setSystem)
      .catch((e) => console.error('System fetch failed', e));
  }, [token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  const isUp = (component) => system?.[component]?.status === 'UP';
  const formatUptime = (seconds) => {
    if (!seconds) return '\u2014';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>{t('monitor.sections.system')}</h1>

        <div style={s.grid}>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.system.connectivity')}</div>
            {['postgres', 'redis', 'netty'].map((svc) => (
              <div key={svc} style={s.statusRow}>
                <span style={s.statusLabel}>
                  <span style={s.statusDot(isUp(svc))} />
                  {t(`monitor.system.service.${svc}`)}
                </span>
                <span style={s.statusBadge(isUp(svc))}>{isUp(svc) ? t('monitor.status.up') : t('monitor.status.down')}</span>
              </div>
            ))}
            {system?.netty?.status === 'DOWN' && system?.netty?.error && (
              <div style={{ ...s.detail, color: '#EF4444' }}>{system.netty.error}</div>
            )}
          </div>

          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.system.wsConnections')}</div>
            <div style={{ display: 'flex', gap: '2rem', marginTop: 8 }}>
              <div>
                <div style={s.metricBig}>{system?.netty?.b2cConnections ?? '\u2014'}</div>
                <div style={s.metricLabel}>{t('monitor.system.b2cCustomers')}</div>
              </div>
              <div>
                <div style={s.metricBig}>{system?.netty?.b2bConnections ?? '\u2014'}</div>
                <div style={s.metricLabel}>{t('monitor.system.b2bStaff')}</div>
              </div>
            </div>
          </div>
        </div>

        <div style={s.grid}>
          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.system.heapMemory')}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={s.metricBig}>{system?.jvm?.heapUsedMb ?? '\u2014'}</span>
              <span style={s.metricUnit}>/ {system?.jvm?.heapMaxMb ?? '\u2014'} MB</span>
            </div>
            <div style={s.progressBar}>
              <div style={s.progressFill(system?.jvm?.heapPercent || 0)} />
            </div>
            <div style={s.detail}>{t('monitor.system.percentUsed', { pct: system?.jvm?.heapPercent ?? 0 })}</div>
          </div>

          <div style={s.card}>
            <div style={s.cardTitle}>{t('monitor.system.system')}</div>
            <div style={s.statusRow}>
              <span style={s.statusLabel}>{t('monitor.system.cpuCores')}</span>
              <span style={{ color: '#F8FAFC', fontWeight: 700 }}>{system?.jvm?.availableProcessors ?? '\u2014'}</span>
            </div>
            <div style={s.statusRow}>
              <span style={s.statusLabel}>{t('monitor.system.loadAverage')}</span>
              <span style={{ color: '#F8FAFC', fontWeight: 700 }}>
                {system?.jvm?.systemLoadAverage >= 0 ? system.jvm.systemLoadAverage.toFixed(2) : 'N/A'}
              </span>
            </div>
            <div style={{ ...s.statusRow, borderBottom: 'none' }}>
              <span style={s.statusLabel}>{t('monitor.system.uptime')}</span>
              <span style={{ color: '#F8FAFC', fontWeight: 700 }}>{formatUptime(system?.jvm?.uptime)}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
